import { Form, Modal, Typography } from 'antd';
import { Formik } from 'formik';
import { useMemo, useState } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import { useExcelUploadModal } from '.';
import { ModuleType } from '../../api/types';
import { type DownloadType, exportVehicles } from '../../utilities/export';
import useHandleError from '../../utilities/useHandleError';
import useValidator from '../../utilities/useValidator';
import validators from '../../utilities/validators';
import { useAccountContext } from '../contexts/AccountContextManager';
import { useCompany } from '../contexts/CompanyContextManager';
import ModuleSelectField from '../fields/ModuleSelectField';
import UserModuleSelectField from '../fields/UserModuleSelectField';
import { ContentContainer } from './ui';

type ModuleSelectModalProps = {
    open: boolean;
    onClose: () => void;
    downloadType: DownloadType;
    importExport: ImportExportType;
    t: TFunction;
};

type FormValues = {
    moduleId?: string;
};

export enum ImportExportType {
    Import = 'import',
    Export = 'export',
}

const formValidator = validators.compose(validators.requiredString('moduleId'));

const ModuleSelectModal = ({ open, onClose, downloadType, importExport, t }: ModuleSelectModalProps) => {
    const company = useCompany(true);
    const { token } = useAccountContext();
    const { displayName, countryCode } = company || {};
    const excelUploadModal = useExcelUploadModal(downloadType);

    const onSubmit = useHandleError<FormValues>(
        async (values, { resetForm }) => {
            if (importExport === ImportExportType.Import) {
                excelUploadModal.open(values.moduleId);
            } else {
                exportVehicles(displayName, countryCode, downloadType, false, values.moduleId, token);
            }
            resetForm();
            onClose();
        },
        [importExport, onClose, excelUploadModal, displayName, countryCode, downloadType, token]
    );

    const validate = useValidator(formValidator);

    return (
        <>
            <Modal
                cancelText={t('importExportExcel:actions.cancel')}
                okButtonProps={{ htmlType: 'submit', form: 'moduleSelectionForm' }}
                okText={t('importExportExcel:actions.continue')}
                onCancel={onClose}
                open={open}
                title={t('importExportExcel:module.title')}
                width={550}
            >
                <Formik initialValues={{ moduleId: null }} onSubmit={onSubmit} validate={validate}>
                    {({ handleSubmit }) => (
                        <Form id="moduleSelectionForm" name="moduleSelectionForm" onSubmitCapture={handleSubmit}>
                            <ContentContainer direction="vertical" size="middle">
                                <Typography.Text>{t('importExportExcel:description.mutipleModules')}</Typography.Text>
                                <UserModuleSelectField
                                    {...t('variantList:vehicleModuleSelectorModal.fields.module', {
                                        returnObjects: true,
                                    })}
                                    moduleType={ModuleType.SimpleVehicleManagement}
                                    name="moduleId"
                                    required
                                />
                            </ContentContainer>
                        </Form>
                    )}
                </Formik>
            </Modal>
            {excelUploadModal.render()}
        </>
    );
};

export default ModuleSelectModal;

export const useModuleSelectModal = (downloadType: DownloadType) => {
    const { t } = useTranslation('importExportExcel');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [importExport, setImportExport] = useState(ImportExportType.Import);

    const actions = useMemo(() => {
        const close = () => setIsModalOpen(false);

        return {
            open: (importExportType: ImportExportType) => {
                setIsModalOpen(true);
                setImportExport(importExportType);
            },
            close,
        };
    }, [setIsModalOpen]);

    return {
        ...actions,
        render: () => (
            <ModuleSelectModal
                downloadType={downloadType}
                importExport={importExport}
                onClose={actions.close}
                open={isModalOpen}
                t={t}
            />
        ),
    };
};
